'use client';

import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';

/**
 * Client-side on-demand translation of a review text via /api/translate
 * (same pattern as the home ReviewCarousel). For locale 'en' the original
 * text is returned untouched and no request is made.
 */
export function useTranslatedText(text: string, locale: string) {
  const [translated, setTranslated] = useState<string | null>(null);
  const [translating, setTranslating] = useState(false);

  useEffect(() => {
    if (!locale || locale === 'en' || !text) return;
    let cancelled = false;
    setTranslating(true);

    fetch(`/api/translate?target=${encodeURIComponent(locale)}&text=${encodeURIComponent(text)}`)
      .then((r) => r.json())
      .then((data) => {
        if (!cancelled && data?.translated) setTranslated(data.translated);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setTranslating(false);
      });

    return () => {
      cancelled = true;
    };
  }, [text, locale]);

  return {
    displayText: translated ?? text,
    translating: translating && translated === null,
  };
}

/** Featured-review body text that translates client-side into the active locale. */
export default function TranslatedText({
  text,
  locale,
  className,
}: {
  text: string;
  locale: string;
  className?: string;
}) {
  const { displayText, translating } = useTranslatedText(text, locale);

  if (translating) {
    return (
      <p className={className}>
        <Loader2 className="inline-block w-5 h-5 animate-spin text-[#9B7B3A]" />
      </p>
    );
  }
  return <p className={className}>&ldquo;{displayText}&rdquo;</p>;
}
